<template>
  <div class="ms-page">
    <!-- Header -->
    <div class="ms-header">
      <div class="ms-header-left">
        <div class="ms-breadcrumb">
          <span>Reports</span>
          <span class="ms-breadcrumb-sep">›</span>
          <span class="ms-breadcrumb-cur">Land Listings</span>
        </div>
        <div class="ms-title-row">
          <h1 class="ms-title">Land Listings</h1>
          <span class="ms-count">{{ items.length.toLocaleString() }} records</span>
        </div>
      </div>
      <div class="ms-header-right">
        <button class="ms-export-btn ms-export-btn--csv" :disabled="!items.length" @click="exportCsv">
          <font-awesome-icon :icon="['fas', 'file-csv']" />
          CSV
        </button>
        <!-- Writes the shown/hidden data points back to the saved view this
             page loads on mount (see loadFilters). -->
        <button class="ms-save-view-btn" title="Save the data points on screen" @click="saveFilters">
          <font-awesome-icon :icon="['fas', 'save']" />
          Save View
        </button>
        <button class="ms-fields-btn" :class="{ 'ms-fields-btn--active': fieldsOpen }"
          @click="fieldsOpen = !fieldsOpen">
          Fields
          <font-awesome-icon :icon="['fas', 'caret-square-down']" />
        </button>
      </div>
    </div>

    <div class="ms-body">
      <!-- Filter bar -->
      <div class="ms-filter-bar">
        <font-awesome-icon :icon="['fas', 'filter']" class="ms-filter-icon" />
        <div class="ms-search-wrap">
          <font-awesome-icon :icon="['fas', 'search']" class="ms-search-icon" />
          <input v-model="filterText" class="ms-search" type="search"
            placeholder="Search address, PID, owner, legal…" />
        </div>
        <span class="ms-count">
          Showing {{ rowCount.toLocaleString() }} of {{ items.length.toLocaleString() }}
        </span>
        <button v-if="filterText" class="ms-filter-clear" @click="filterText = ''">
          <font-awesome-icon :icon="['fas', 'times']" />
          Clear
        </button>
      </div>

      <!-- Fields panel -->
      <transition name="ms-slide">
        <div v-if="fieldsOpen" class="ms-fields-panel">
          <div class="ms-fields-col">
            <div class="ms-fields-col-header">
              <span class="ms-fields-col-title">Shown Data Points</span>
              <button class="ms-fields-reset" title="Reset to default" @click="resetFields">
                <font-awesome-icon :icon="['fas', 'undo']" />
              </button>
              <button class="ms-fields-close" @click="fieldsOpen = false">
                <font-awesome-icon :icon="['fas', 'times-circle']" class="text-danger" />
              </button>
            </div>
            <draggable class="ms-drag-list" :list="fields" group="ms-fields" @change="fieldsOn">
              <div v-for="(field, i) in fields" :key="'shown-' + field.key"
                class="ms-drag-item ms-drag-item--active">
                <font-awesome-icon :icon="['fas', 'grip-vertical']" class="ms-drag-handle" />
                <span class="ms-drag-index">{{ i + 1 }}.</span>
                {{ field.label }}
                <!-- Admin-only: this data point can be edited inline in the table -->
                <span v-if="isAdmin && isEditableField(field.key)" class="ms-drag-editable"
                  title="Editable — double-click the cell in the table to change it">
                  <font-awesome-icon :icon="['fas', 'pen']" />
                  Editable
                </span>
              </div>
            </draggable>
          </div>

          <div class="ms-fields-divider"></div>

          <div class="ms-fields-col">
            <div class="ms-fields-col-header">
              <span class="ms-fields-col-title">Drag and Drop Data Points</span>
            </div>
            <draggable class="ms-drag-list" :list="rpdTableList2" group="ms-fields" @change="fieldsOn">
              <div v-for="field in rpdTableList2" :key="'hidden-' + field.key" class="ms-drag-item">
                <font-awesome-icon :icon="['fas', 'grip-vertical']" class="ms-drag-handle" />
                {{ field.label }}
                <span v-if="isAdmin && isEditableField(field.key)" class="ms-drag-editable"
                  title="Editable — double-click the cell in the table to change it">
                  <font-awesome-icon :icon="['fas', 'pen']" />
                  Editable
                </span>
              </div>
            </draggable>
            <div v-if="!rpdTableList2.length" class="ms-fields-hint">
              Every data point is shown. Drag one here to hide it.
            </div>
          </div>
        </div>
      </transition>

      <!-- Loading -->
      <div v-if="loading" class="ms-state">
        <div class="ms-spinner"></div>
        <span>Loading land listings…</span>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="ms-state ms-state--error">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
        {{ error }}
      </div>

      <!-- Table -->
      <div class="ms-table-wrap" :class="{ 'ms-hidden': loading || error }">
        <div ref="table"></div>
      </div>
    </div>

    <b-modal id="modal-legal" title="Full Legal Description" ok-only scrollable>
      <p class="ms-legal-text">{{ selectedLegalDescription }}</p>
    </b-modal>
  </div>
</template>

<script>
import { markRaw } from "vue";
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { wrapColumns } from "../utilities/tabulatorSorter";
import 'tabulator-tables/dist/css/tabulator.min.css';
import { BACKEND_URL } from '../variables';
import draggable from "vuedraggable";
import { saveView, loadView } from "../composables/asyncView.js";
import { useUserStore } from '../store/UserStore.js';
import { mapState } from 'pinia';
import axios from "axios";

// The data points an admin can change inline. Drives three things at once so
// they can never drift apart: which columns get an editor, which get the tinted
// "editable" header, and which are posted by handleRowSave.
const EDITABLE_FIELDS = [
  'field_site_address',
  'field_ltsa_pid',
  'field_lot_size_sqft_',
  'field_legal_description',
  'field_bc_assessment_sale_type',
  'field_land_type',
  'field_existing_use_ref',
  'field_existing_zoning_d',
  'field_city_based_planning_design',
  'field_land_list_date',
  'field_land_list_price',
  'field_ltsa_registered_owner',
  'field_sold_firm_date',
  'field_land_sale_price',
];

// These four hang off the land sale node and post to api/land_sale/edit; every
// other editable field belongs to the title registration (see handleRowSave).
const LAND_SALE_FIELDS = [
  'field_site_address',
  'field_ltsa_pid',
  'field_lot_size_sqft_',
  'field_legal_description',
];

const NUMERIC_FIELDS = ['field_lot_size_sqft_', 'field_land_list_price', 'field_land_sale_price'];
const CURRENCY_FIELDS = ['field_land_list_price', 'field_land_sale_price'];
const DATE_FIELDS = ['field_land_list_date', 'field_sold_firm_date'];
const LIST_FIELDS = [
  'field_land_type',
  'field_bc_assessment_sale_type',
  'field_existing_use_ref',
  'field_city_based_planning_design',
];

// Columns the search box looks in. The placeholder names them, so the two have
// to be read together.
const SEARCH_FIELDS = [
  'field_site_address',
  'field_ltsa_pid',
  'field_ltsa_registered_owner',
  'field_legal_description',
];

// The table's columns, in their default order. `width` is a minimum — the table
// stretches past it (fitDataStretch) — and `align: 'right'` marks the figures.
const DEFAULT_FIELDS = [
  { key: "field_site_address", label: "Address", width: 240 },
  { key: "field_ltsa_pid", label: "PID", width: 130 },
  { key: "field_legal_description", label: "Legal Description", width: 260 },
  { key: "field_lot_size_sqft_", label: "Lot Size", align: 'right' },
  { key: "field_land_type", label: "Land Type" },
  { key: "field_ltsa_registered_owner", label: "Owner", width: 180 },
  { key: "field_existing_zoning_d", label: "Zoning" },
  { key: "field_bc_assessment_sale_type", label: "Sale Type" },
  { key: "field_land_list_date", label: "List Date" },
  { key: "field_land_list_price", label: "List Price", align: 'right' },
  { key: "field_first_broker_agent", label: "Broker", width: 160 },
  { key: "field_sold_firm_date", label: "Sold Date" },
  { key: "field_land_sale_price", label: "Sale Price", align: 'right' },
  { key: "field_sold_broker_agent", label: "Sold Broker", width: 160 },
  { key: "field_existing_use_ref", label: "Existing Use" },
  { key: "field_city_based_planning_design", label: "Planning Desig.", width: 180 },
];

// Nothing to report for this cell. The em dash keeps a column's rows aligned in
// a way a blank cell does not.
const dash = () => '<span class="ms-empty">—</span>';

const escapeHtml = (v) => String(v)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

export default {
  name: "LandList",
  components: { draggable },
  data() {
    return {
      filterText: "",
      tabulator: null,
      items: [],
      rowCount: 0,
      loading: false,
      error: "",
      fieldsOpen: false,
      selectedLegalDescription: "",
      fields: DEFAULT_FIELDS.map((f) => ({ ...f })),
      rpdTableList2: [
      ],
      landTypeOptions: [
        {
          label: 'Commercial',
          value: '23481'
        },
        {
          label: 'Industrial',
          value: '23482'
        },
        {
          label: 'Residential',
          value: '23480'
        }
      ],
      saleTypeOptions: [
        {
          label: 'Listing',
          value: '11453'
        },
        {
          label: 'Share Transfer',
          value: '11244'
        },
        {
          label: 'Sold Firm',
          value: '11243'
        },
        {
          label: 'Sold Registered',
          value: '11520'
        },
        {
          label: 'Title Cancel',
          value: '11432'
        },
        {
          label: 'Under Contract',
          value: '23498'
        }
      ],
      existingUseOptions: [
        {
          label: 'Agriculture',
          value: '3512'
        },
        {
          label: 'Heritage',
          value: '3561'
        },
        {
          label: 'Park',
          value: '3623'
        },
        {
          label: 'Townhomes',
          value: '3624'
        },
        {
          label: 'Apartment',
          value: '3510'
        },
        {
          label: 'Below-Market Rental',
          value: '11253'
        },
        {
          label: 'Senior Rental',
          value: '11255'
        },
        {
          label: 'Senior Strata',
          value: '11256'
        },
        {
          label: 'Market Rental',
          value: '11252'
        },
        {
          label: 'Market Strata',
          value: '11254'
        },
        {
          label: 'Commercial',
          value: '3509'
        },
        {
          label: 'Hotel',
          value: '3514'
        },
        {
          label: 'Industrial',
          value: '3513'
        },
        {
          label: 'Parkade',
          value: '3511'
        },
        {
          label: 'Single Family',
          value: '3508'
        },
        {
          label: 'Duplex',
          value: '7966'
        },
        {
          label: 'Fourplex',
          value: '7967'
        },
        {
          label: 'Vacant',
          value: '3507'
        }
      ],
      cityBasedPlanningDesignationOptions: [
        { label: 'Transit Oriented Development', value: '23499' },
        { label: 'Mission', value: '4077' },
        { label: 'Land Use', value: '20735' },
        { label: 'Single-Family Residential', value: '20736' },
        { label: 'Multi-Family Residential', value: '20737' },
        { label: 'Mobile Homes and Trailer Parks', value: '20738' },
        { label: 'Mixed Residential', value: '20739' },
        { label: 'Rural Residential', value: '20740' },
        { label: 'General Office', value: '20741' },
        { label: 'Commercial and Services', value: '20742' },
        { label: 'Facilities', value: '20743' },
        { label: 'Education', value: '20744' },
        { label: 'Military Installations', value: '20745' },
        { label: 'Industrial', value: '20746' },
        { label: 'Transportation, Communications, and Utilities', value: '20747' },
        { label: 'Mixed Commercial and Industrial', value: '20748' },
        { label: 'Mixed Residential and Commercial', value: '20749' },
        { label: 'Open Space and Recreation', value: '20750' },
        { label: 'Agriculture', value: '20751' },
        { label: 'Vacant', value: '20752' },
        { label: 'Maple Ridge', value: '20667' },
        { label: 'Area Plan', value: '20672' },
        { label: 'Albion Area Plan', value: '20668' },
        { label: 'Hammond Area Plan', value: '20671' },
        { label: 'Silver Valley Area Plan', value: '20669' },
        { label: 'Town Centre Area Plan', value: '20670' },
        { label: 'Land Use', value: '20688' },
        { label: 'Albion Area Plan', value: '20689' },
        { label: 'Single-Family Residential', value: '20690' },
        { label: 'Low Density Residential', value: '20691' },
        { label: 'Low Medium Density Residential', value: '20692' },
        { label: 'Medium Density Residential', value: '20693' },
        { label: 'Conservation', value: '20694' },
        { label: 'Park', value: '20695' },
        { label: 'Institutional', value: '20696' },
        { label: 'Village Commercial', value: '20697' },
        { label: 'Neighbourhood Commercial', value: '20698' },
        { label: 'Silver Valley Area Plan', value: '20699' },
        { label: 'Eco Clusters', value: '20700' },
        { label: 'Low Density Urban', value: '20701' },
        { label: 'Low/Medium Density Residential', value: '20702' },
        { label: 'Medium Density Residential', value: '20703' },
        { label: 'Med/High Density Residential', value: '20704' },
        { label: 'High Density Residential', value: '20705' },
        { label: 'Commercial', value: '20706' },
        { label: 'Neighbourhood Park', value: '20707' },
        { label: 'Open Space', value: '20708' },
        { label: 'Civic', value: '20709' },
        { label: 'Conservation', value: '20710' },
        { label: 'Tourist', value: '20711' },
        { label: 'Town Centre Area Plan', value: '20712' },
        { label: 'Single-Family Residential', value: '20713' },
        { label: 'Port Haney Heritage Adaptive Use', value: '20714' },
        { label: 'Port Haney Multi-Family, Commercial and Mixed-Use', value: '20715' },
        { label: 'Ground-Oriented Multi-Family', value: '20716' },
        { label: 'Medium and High-Rise Apartment', value: '20717' },
        { label: 'Low-Rise Apartment', value: '20718' },
        { label: 'Flexible Mixed-Use', value: '20719' },
        { label: 'Town Centre Commercial', value: '20720' },
        { label: 'Institutional', value: '20721' },
        { label: 'Conservation', value: '20722' },
        { label: 'Park', value: '20723' },
        { label: 'Hammond Area Plan', value: '20724' },
        { label: 'Single-Family & Compact Residential', value: '20725' },
        { label: 'Low Density Multi-Family', value: '20726' },
        { label: 'Medium Density Multi-Family', value: '20727' },
        { label: 'Hammond General Employment', value: '20730' },
        { label: 'Infill General Employment', value: '20729' },
        { label: 'Hammond Village Commercial', value: '20728' },
        { label: 'Maple Meadows Business Park', value: '20731' },
        { label: 'Institutional', value: '20732' },
        { label: 'Park & Open Space', value: '20733' },
        { label: 'Conservation', value: '20734' },
        { label: 'Surrey', value: '3865' },
        { label: 'Land Use', value: '20673' },
        { label: 'Conservation and Recreation', value: '20674' },
        { label: 'Agricultural', value: '20675' },
        { label: 'Rural', value: '20676' },
        { label: 'Suburban', value: '20677' },
        { label: 'Suburban - Urban Reserve', value: '20678' },
        { label: 'Urban', value: '20679' },
        { label: 'Multiple Residential', value: '20680' },
        { label: 'Commercial', value: '20681' },
        { label: 'Industrial', value: '20682' },
        { label: 'Mixed Employment', value: '20683' },
        { label: 'Town Centre', value: '20684' },
        { label: 'Downtown', value: '20685' },
        { label: 'First Nation Reserve', value: '20686' },
        { label: 'Area Plan', value: '20687' },
        { label: 'Commercial', value: '6424' },
        { label: 'Industrial', value: '6423' },
        { label: 'Fleetwood', value: '3879' },
        { label: 'Fleetwood Enclave Infill Plan', value: '3880' },
        { label: 'Fleetwood Plan', value: '3994' },
        { label: 'Fleetwood Town Centre', value: '3992' },
        { label: 'West Fleetwood', value: '3993' },
        { label: 'South Surrey', value: '3905' },
        { label: 'Crescent Beach LAP', value: '3906' },
        { label: 'Darts Hill', value: '11133' },
        { label: 'Darts Hill NCP', value: '11134' },
        { label: 'Douglas NCP', value: '3907' },
        { label: 'Highway 99 Corridor LAP', value: '3908' },
        { label: 'King George Corridor - South LAP', value: '3909' },
        { label: 'Campbell Heights', value: '3910' },
        { label: 'Campbell Heights LAP', value: '3911' },
        { label: 'South Campbell Heights LAP', value: '3912' },
        { label: 'Grandview Heights', value: '3913' },
        { label: 'Grandview Heights General Land Use Plan (GLUP)', value: '3914' },
        { label: 'North Grandview Heights NCP', value: '3915' },
        { label: 'Morgan Heights NCP', value: '3916' },
        { label: 'Orchard Grove NCP', value: '3917' },
        { label: 'Sunnyside Heights NCP', value: '3918' },
        { label: 'Redwood Heights NCP', value: '3919' },
        { label: 'Rosemary Heights', value: '3920' },
        { label: 'Rosemary Heights Business Park Plan', value: '3921' },
        { label: 'Rosemary Heights Central NCP', value: '3922' },
        { label: 'Rosemary Heights West NCP', value: '3923' },
        { label: 'Semiahmoo Town Centre', value: '3924' },
        { label: 'Newton', value: '3889' },
        { label: 'East Newton', value: '3890' },
        { label: 'East Newton North NCP', value: '3892' },
        { label: 'East Newton South NCP', value: '3893' },
        { label: 'East Newton Business Park Plan', value: '3891' },
        { label: 'South Newton', value: '3894' },
        { label: 'East Panorama Ridge Plan', value: '3895' },
        { label: 'South Newton NCP', value: '3896' },
        { label: 'West Newton', value: '3897' },
        { label: 'Newton Town Centre Plan', value: '3903' },
        { label: 'West Newton/Hwy 10 NCP', value: '3899' },
        { label: 'West Newton South NCP', value: '3901' },
        { label: 'West Newton North NCP', value: '3900' },
        { label: 'Newton-King George Boulevard Plan', value: '3904' },
        { label: 'Newton Cultural Commercial District', value: '3898' },
        { label: 'Guildford', value: '3881' },
        { label: 'Fraser Heights', value: '3882' },
        { label: 'Abbey Ridge LAP', value: '3883' },
        { label: 'Guildford Town', value: '3887' },
        { label: 'Guildford Town Centre & 104 Avenue Corridor', value: '3888' },
        { label: 'South Port Kells', value: '3884' },
        { label: 'South Port Kells GLUP', value: '3885' },
        { label: 'Anniedale-Tynehead NCP', value: '3886' },
        { label: 'Whalley', value: '3926' },
        { label: 'Surrey City Centre Plan', value: '3928' },
        { label: 'Central Business District', value: '4117' },
        { label: 'South Westminster Heights Infill', value: '3930' },
        { label: 'South Westminster NCP', value: '3929' },
        { label: 'Cloverdale', value: '3866' },
        { label: 'Cloverdale Town Centre', value: '3867' },
        { label: 'Clayton Heights', value: '3868' },
        { label: 'Clayton General Land Use Plan (GLUP)', value: '3869' },
        { label: 'East Clayton NCP', value: '3870' },
        { label: 'Aloha Estates Infill Plan', value: '3871' },
        { label: 'West Clayton NCP', value: '3872' },
        { label: 'North Cloverdale', value: '3873' },
        { label: 'North Cloverdale East NCP', value: '3874' },
        { label: 'North Cloverdale West NCP', value: '3875' },
        { label: 'West Cloverdale', value: '3876' },
        { label: 'West Cloverdale South NCP', value: '3878' },
        { label: 'West Cloverdale North NCP', value: '3877' },
        { label: 'White Rock', value: '4022' },
        { label: 'Land Use', value: '20661' },
        { label: 'Town Centre', value: '4023' },
        { label: 'Town Centre Transition', value: '20662' },
        { label: 'Lower Town Centre', value: '4024' },
        { label: 'Waterfront', value: '4025' },
        { label: 'Urban Neighbourhood', value: '20663' },
        { label: 'North Bluff East', value: '20664' },
        { label: 'Mature Neighbourhood Infill', value: '4029' },
        { label: 'East Side Large Lot Infill', value: '4028' },
        { label: 'Neighbourhood Commercial', value: '4027' },
        { label: 'Institutional', value: '20665' },
        { label: 'Open Space & Recreation', value: '20666' },
        { label: 'Multi-Family', value: '4026' },
        { label: 'Vancouver', value: '5793' },
        { label: 'Official Development Plan', value: '10475' },
        { label: 'Arbutus Corridor', value: '10527' },
        { label: 'Central Waterfront', value: '10528' },
        { label: 'Coal Harbour Official Development Plan', value: '6012' },
        { label: 'Downtown', value: '10529' },
        { label: 'East Fraser Lands', value: '10531' },
        { label: 'False Creek', value: '10532' },
        { label: 'False Creek North', value: '10533' },
        { label: 'Heritage Conservation Area', value: '10534' },
        { label: 'Southeast False Creek ODP', value: '5802' },
        { label: 'Southeast Granville Slopes', value: '10535' },
        { label: 'Policies', value: '10539' },
        { label: 'Citywide', value: '10471' },
        { label: 'CityPlan (1995)', value: '10536' },
        { label: 'Housing Vancouver (2017)', value: '10537' },
        { label: 'Metro Core Jobs and Economy Land Use Plan (2007)', value: '10472' },
        { label: 'Area specific plans', value: '10580' },
        { label: 'Arbutus Neighbourhood Policy Plan (1992)', value: '10538' },
        { label: 'Arbutus Ridge/Kerrisdale/Shaughnessy Community Vision', value: '5927' },
        { label: 'Cambie Corridor Plan', value: '5911' },
        { label: 'Cambie Corridor Plan (Phase 1)', value: '5798' },
        { label: 'Cambie Corridor Plan (Phase 2)', value: '5799' },
        { label: 'Cambie Corridor Plan (Phase 3)', value: '5800' },
        { label: 'Cedar Cottage MC-1/Welwyn Street Plan (1996)', value: '10540' },
        { label: 'Central Area Plan (1991)', value: '10581' },
        { label: 'Chinatown Neighbourhood Plan (2012)', value: '10541' },
        { label: 'Downtown Eastside Plan', value: '5794' },
        { label: 'Dunbar-Southlands Community Vision (1998)', value: '10542' },
        { label: 'East Vancouver Port Lands Area Plan (2007)', value: '10543' },
        { label: 'False Creek Flats Plan (2017)', value: '10544' },
        { label: 'Grandview Boundary Mixed Employment Area Plan (2002)', value: '10545' },
        { label: 'Granville Island Reference Document (1978)', value: '10546' },
        { label: 'Granville Loops Policy Plan (2010)', value: '10547' },
        { label: 'Grandview-Woodland Community Plan (2016)', value: '5805' },
        { label: 'Kensington-Cedar Cottage Community Vision (1998)', value: '5937' },
        { label: 'Kingsway and Knight Neighbourhood Centre Plan (2004)', value: '10550' },
        { label: 'Kitsilano Neighbourhood Plan Summary (1977)', value: '10551' },
        { label: 'Marpole Community Plan', value: '5801' },
        { label: 'Mount Pleasant Community Plan', value: '5807' },
        { label: 'Nanaimo/29th Avenue Station Area Plan Summary (1987)', value: '10552' },
        { label: 'Norquay Village Neighbourhood Centre Plan (2010)', value: '5808' },
        { label: 'Northeast False Creek Plan (2018)', value: '5940' },
        { label: 'Renfrew-Collingwood Community Vision (2004)', value: '10553' },
        { label: 'Riley Park/South Cambie Community Vision (2005)', value: '10554' },
        { label: 'Southlands Plan (1998)', value: '10555' },
        { label: 'Still Creek Enhancement Plan (2002)', value: '10556' },
        { label: 'Sunset Community Vision (2002)', value: '10557' },
        { label: 'Victoria-Fraserview/Killarney Community Vision (2002)', value: '10558' },
        { label: 'Victory Square Policy Plan (2005)', value: '10559' },
        { label: 'West End Community Plan (2013)', value: '10560' },
        { label: 'West Point Grey Community Vision (2010)', value: '10561' },
        { label: 'Rezoning Policies', value: '10576' },
        { label: 'Citywide', value: '10583' },
        { label: 'Metro Core Jobs and Economy Land Use Plan (2007)', value: '10584' },
        { label: 'Moderate income rental housing', value: '10473' },
        { label: 'Secured rental', value: '10474' },
        { label: 'Area Specific', value: '10579' },
        { label: 'Broadway-Arbutus', value: '10476' },
        { label: 'Broadway Plan area', value: '10477' },
        { label: 'Broadway area west of Vine Street', value: '10478' },
        { label: 'Burrard Slopes IC districts', value: '10479' },
        { label: 'Cambie Corridor', value: '10585' },
        { label: 'Central Business District and shoulder', value: '10480' },
        { label: 'Downtown South', value: '10481' },
        { label: 'Downtown Eastside', value: '10586' },
        { label: 'False Creek Flat Area Plan', value: '5804' },
        { label: 'Grandview Boundary mixed employment area', value: '10482' },
        { label: 'Grandview-Woodland', value: '10587' },
        { label: 'Granville Slopes', value: '10483' },
        { label: 'I-2 and M-2 non-industrial uses', value: '10484' },
        { label: 'Joyce-Collingwood Station Precinct Plan', value: '5806' },
        { label: 'Little Mountain Adjacent Area Rezoning Policy', value: '5938' },
        { label: 'Marine Drive Industrial Area', value: '10485' },
        { label: 'Marpole', value: '10588' },
        { label: 'Mount Pleasant', value: '10589' },
        { label: 'Southlands', value: '10486' },
        { label: 'West End Plan', value: '5797' },
        { label: 'Norquay Village', value: '10590' },
        { label: 'Policy Statements', value: '10562' },
        { label: 'Arbutus Centre Policy Statement', value: '6013' },
        { label: 'Central Waterfront Port Lands (1994)', value: '10563' },
        { label: 'Coal Harbour (1990)', value: '10564' },
        { label: 'East Fraserlands (2004)', value: '10582' },
        { label: 'Heather Lands', value: '10565' },
        { label: 'Langara College (2005)', value: '10566' },
        { label: 'Langara Gardens (2018)', value: '10567' },
        { label: 'Little Mountain (2012)', value: '10568' },
        { label: 'Oakridge Centre (2007)', value: '10569' },
        { label: 'Oakridge Transit Centre and adjacent sites (2015)', value: '10570' },
        { label: 'Pearson Dogwood (2014)', value: '10571' },
        { label: "St. Paul's Hospital and Health Campus (2017)", value: '10572' },
        { label: 'Southeast False Creek (1999)', value: '10573' },
        { label: 'Under the Granville Bridge neighbourhood (2007)', value: '10574' },
        { label: 'Vancouver Community College King Edward Campus (2004)', value: '10575' },
        { label: 'Affordable Housing Choices Interim Rezoning Policy', value: '5908' },
        { label: 'Below-Market Rental Housing Policy', value: '5928' },
        { label: 'Broadway East Revitalization Strategy', value: '5803' },
        { label: 'Cultural Facilities Priority Plan', value: '5929' },
        { label: 'Downtown Eastside/Oppenheimer', value: '10530' },
        { label: 'Downtown Official Development Plan', value: '5795' },
        { label: 'Downtown Potential Benefit Capacity Policy', value: '6016' },
        { label: 'Dunbar Community Vision', value: '5930' },
        { label: 'East Fraser Lands Official Development Plan', value: '5931' },
        { label: 'False Creek North Official Development Plan', value: '5932' },
        { label: 'Heritage Policies and Guidelines and Heritage Action Plan', value: '5934' },
        { label: 'Killarney', value: '11123' },
        { label: 'Secured rental', value: '10577' },
        { label: 'Higher Buildings Policy', value: '5935' },
        { label: 'Housing and Homelessness Strategy', value: '5936' },
        { label: 'Green Buildings Policy for Rezonings', value: '5933' },
        { label: 'Moderate Income Rental Housing Pilot Program', value: '5939' },
        { label: 'Northeast False Creek Refined Area Plan', value: '5941' },
        { label: 'Oakridge Langara Policy Statement', value: '5942' },
        { label: 'Joyce Station Area Plan Summary (1987)', value: '10549' },
        { label: 'Rental 100', value: '5809' },
        { label: 'Hastings-Sunrise Community Vision (2004)', value: '10548' },
        { label: 'Strathcona', value: '11124' },
        { label: 'Secured Market Rental Housing Policy', value: '5909' },
        { label: 'Short-Term Incentives for Rental', value: '5810' },
        { label: 'Vancouver OCP', value: '5796' },
        { label: 'General Policies', value: '10578' },
        { label: 'Victoria-Fraserview/Killaney Community Vision', value: '5943' },
        { label: 'Victory Square Policy Plan', value: '5944' },
        { label: 'West Vancouver', value: '20651' },
        { label: 'Land Use', value: '20652' },
        { label: 'Infill', value: '20653' },
        { label: 'Multiplex', value: '20654' },
        { label: 'Rowhouse', value: '20655' },
        { label: 'Village Townhouse', value: '20656' },
        { label: 'Village Heart', value: '20657' },
        { label: 'Bay St Residential', value: '20658' },
        { label: 'Nelson Ave Apartment', value: '20659' },
        { label: 'Keith Road Mixed-Use', value: '20660' },
        { label: 'Local Area Plan', value: '21090' },
        { label: 'Marine Drive Area Plan', value: '21091' },
        { label: 'Ambleside Town Centre Local Area Plan', value: '21092' },
        { label: 'Horseshoe Bay Local Area Plan', value: '21093' },
        { label: 'Taylor Way Local Area Plan', value: '21094' },
        { label: 'Cypress Village Plan Area', value: '21095' },
        { label: 'Cypress West Plan Area', value: '21096' },
        { label: 'Delta', value: '4043' },
        { label: 'Area Plan', value: '20642' },
        { label: 'Ladner', value: '20643' },
        { label: 'Riverside Area Plan', value: '20645' },
        { label: 'East Ladner Area Plan', value: '20644' },
        { label: 'Ladner Area Plan', value: '20646' },
        { label: 'North Delta', value: '20647' },
        { label: 'North Delta Area Plan', value: '20648' },
        { label: 'Tsawwassen', value: '20649' },
        { label: 'Tsawwassen Area Plan', value: '20650' },
        { label: 'Land Use', value: '20627' },
        { label: 'Single Family Residential', value: '20628' },
        { label: 'Medium Density Residential', value: '20629' },
        { label: 'Townhouse Residential', value: '20630' },
        { label: 'Commercial', value: '20631' },
        { label: 'Town Center', value: '20632' },
        { label: 'Service', value: '20633' },
        { label: 'Residential', value: '20634' },
        { label: 'Marina/Resort', value: '20635' },
        { label: 'Industrial', value: '20636' },
        { label: 'Institutional (Schools)', value: '20637' },
        { label: 'Major Parks and Recreational Areas', value: '20638' },
        { label: 'Agricultural (ALR)', value: '20639' },
        { label: 'Agricultural - Residential Study Area', value: '20640' },
        { label: 'Resource Study Area', value: '20641' },
        { label: 'Pitt Meadows', value: '20586' },
        { label: 'Land Use', value: '20616' },
        { label: 'Agricultural', value: '20587' },
        { label: 'Agricultural', value: '20588' },
        { label: 'Residential', value: '20589' },
        { label: 'Residential - Low Density', value: '20590' },
        { label: 'Residential - Medium Density', value: '20591' },
        { label: 'Residential - High Density', value: '20592' },
        { label: 'Rural Residential', value: '20593' },
        { label: 'Mixed-Use', value: '20594' },
        { label: 'Town Centre', value: '20595' },
        { label: 'Village', value: '20596' },
        { label: 'Commercial', value: '20597' },
        { label: 'Highway Commercial', value: '20598' },
        { label: 'Community Commercial', value: '20599' },
        { label: 'Outdoor Recreation', value: '20600' },
        { label: 'Industrial', value: '20603' },
        { label: 'Business Park', value: '20602' },
        { label: 'Industrial', value: '20601' },
        { label: 'Resource Management', value: '20604' },
        { label: 'Utilities and Transportation', value: '20605' },
        { label: 'Airport', value: '20606' },
        { label: 'Public/Institutional', value: '20607' },
        { label: 'Institutional', value: '20608' },
        { label: 'Civic Centre', value: '20609' },
        { label: 'Heritage Preservation Area', value: '20610' },
        { label: 'Parks & Open Space', value: '20611' },
        { label: 'Conservation Area', value: '20612' },
        { label: 'Wildlife Management Area', value: '20613' },
        { label: 'Open Space', value: '20614' },
        { label: 'Park', value: '20615' },
        { label: 'New Westminster', value: '20529' },
        { label: 'Land Use', value: '20530' },
        { label: 'Mainland Community Plan', value: '20531' },
        { label: '(RD) Residential - Detached and Semi-Detached Housing', value: '20532' },
        { label: '(RGO) Residential-Ground Oriented Infill Housing', value: '20533' },
        { label: '(RT) Residential - Infill Townhouse', value: '20534' },
        { label: '(RM) Residential - Multiple Unit Buildings', value: '20535' },
        { label: '(RH) Residential - High Rise', value: '20536' },
        { label: '(ML) Mixed Use - Low Rise', value: '20537' },
        { label: '(MH) Mixed Use - High Rise', value: '20538' },
        { label: '(BDMU) Brewery District Mixed Use and Health Care', value: '20539' },
        { label: '(SGTMC) Sapperton green Transit-Oriented Mixed-Use Community', value: '20540' },
        { label: '(RHC) Residential - High Dendity / Community Facility', value: '20541' },
        { label: '(CHC) Commercial and Health Care', value: '20542' },
        { label: '(C) Commercial', value: '20543' },
        { label: '(ME) Mixed Employment', value: '20544' },
        { label: '(I) Industrial', value: '20545' },
        { label: '(U) Utilities and Transportation Infrastructure', value: '20546' },
        { label: '(IN) Intertidal', value: '20547' },
        { label: '(H/N) Habitat / Natural Areas', value: '20548' },
        { label: '(CEM) Cemeteries', value: '20549' },
        { label: '(P) Major Institutional', value: '20550' },
        { label: 'Parks, Open Space and Community Facilities', value: '20551' },
        { label: '(BC) Bent Court Study Area', value: '20552' },
        { label: '(LTS) Lower Twelfth and Sharpe Street Study Area', value: '20553' },
        { label: '(CD) Comprehensive Development', value: '20554' },
        { label: 'Queensborough Community Plan', value: '20555' },
        { label: '(RL) Residential Low-Density', value: '20556' },
        { label: '(RCL) Residential - Compact Lot', value: '20557' },
        { label: '(RM) Residential - Multiple Unit Buildings', value: '20558' },
        { label: '(RH) Residential - High Rise', value: '20559' },
        { label: '(ML) Mixed Use - Low Rise', value: '20560' },
        { label: '(QC) Queensborough Commercial', value: '20561' },
        { label: '(CE) Commercial Entertainment', value: '20562' },
        { label: '(QME) Queensborough Mixed', value: '20563' },
        { label: '(I) Industrial', value: '20564' },
        { label: '(IN) Intertidal', value: '20565' },
        { label: '(WR) Waterfront residential', value: '20566' },
        { label: '(P) Major Institutional', value: '20567' },
        { label: 'Parks, Open Space and Community Facilities', value: '20568' },
        { label: '(H/N) Habitat / Natural Areas', value: '20569' },
        { label: '(U) Urilities and Transportation Infrastructure', value: '20570' },
        { label: '(QCD) Queensborough Comprehensive Development', value: '20571' },
        { label: 'Downtown Community Plan', value: '20572' },
        { label: 'Residential Low-Rise Apartment', value: '20573' },
        { label: 'Residential - Mid Rise Apartment', value: '20574' },
        { label: 'Residential - Tower Apartment', value: '20575' },
        { label: 'Columbia Historic Mixed Use', value: '20576' },
        { label: 'Mixed Use High Density', value: '20577' },
        { label: 'Commercial Waterfront', value: '20578' },
        { label: 'Comprehensive Development', value: '20579' },
        { label: 'Irving House Cultural Commercial', value: '20580' },
        { label: 'Muni Evers Resedential/Park', value: '20581' },
        { label: 'Pattullo Bridge Realignment', value: '20582' },
        { label: 'School/Institution', value: '20583' },
        { label: 'Parks & Open Space', value: '20584' },
        { label: 'Transportation Infrastructure', value: '20585' },
        { label: 'Port Moody', value: '6350' },
        { label: 'Land Use', value: '7348' },
        { label: 'Single Family Low Density', value: '7345' },
        { label: 'High-Rise Residential', value: '7346' },
        { label: 'Multi-Family Residential', value: '7347' },
        { label: 'Mixed Use - Inlet Centre', value: '7349' },
        { label: 'Mixed Use - Moody Centre', value: '7350' },
        { label: 'Mixed Use - Marina', value: '7351' },
        { label: 'Mixed Use - Oceanfront District', value: '7353' },
        { label: 'Mixed Use - Woodland Park', value: '20528' },
        { label: 'Moody Centre Transit Oriented Development', value: '7352' },
        { label: 'Mixed Employment', value: '7354' },
        { label: 'Parks and Open Space', value: '7355' },
        { label: 'Public and Institutional', value: '7356' },
        { label: 'Industrial Business', value: '7357' },
        { label: 'General Industrial', value: '7358' },
        { label: 'Neighbourhood Commercial', value: '7359' },
        { label: 'Special Study Area', value: '7360' },
        { label: 'Burnaby', value: '4239' },
        { label: 'Area Plan', value: '20527' },
        { label: 'Bainbridge', value: '4255' },
        { label: 'Big Bend', value: '4273' },
        { label: 'Brentwood Town Centre', value: '4242' },
        { label: 'Brentwood Town Centre', value: '4250' },
        { label: 'Brentwood Town Centre Master Plan', value: '4245' },
        { label: 'Gilmore Station Conceptual Master Plan', value: '4244' },
        { label: 'Grove Master Plan', value: '4246' },
        { label: 'Woodlands Conceptual Master Plan', value: '4243' },
        { label: 'Brighton', value: '4256' },
        { label: 'Broadview', value: '4257' },
        { label: 'Burnaby Lake Sports Complex', value: '4274' },
        { label: 'Capitol Hill', value: '4258' },
        { label: 'Cariboo Heights', value: '4266' },
        { label: 'Cascade Heights', value: '4259' },
        { label: 'Central Administrative Area', value: '4275' },
        { label: 'Discovery Place', value: '4276' },
        { label: 'Edmonds Town Centre', value: '4247' },
        { label: 'Edmonds Town Centre', value: '4249' },
        { label: 'Southgate Neighbourhood Master Plan', value: '4248' },
        { label: 'Forest Grove', value: '4267' },
        { label: 'George Derby', value: '4268' },
        { label: 'Greentree Village', value: '4269' },
        { label: 'Heights', value: '4260' },
        { label: 'Holdom', value: '4277' },
        { label: 'Lake City', value: '4278' },
        { label: 'Lochdale', value: '4261' },
        { label: 'Lougheed Town Centre', value: '4252' },
        { label: 'Lougheed Town Centre', value: '4253' },
        { label: 'Lougheed Town Centre Core Area Master Plan', value: '4254' },
        { label: 'Metrotown - Regional Town Centre', value: '4240' },
        { label: 'Metrotown Downtown Plan', value: '4241' },
        { label: 'Montecito', value: '4262' },
        { label: 'Myrtle Street', value: '4279' },
        { label: 'Newcombe', value: '4270' },
        { label: 'Oaklands', value: '4271' },
        { label: 'Rayside', value: '4272' },
        { label: 'Royal Oak', value: '4263' },
        { label: 'Simon Fraser University', value: '4280' },
        { label: 'Sixth Street', value: '4264' },
        { label: 'Willingdon Heights', value: '4265' },
        { label: 'SFU Community', value: '6046' },
        { label: 'Hastings Street', value: '6047' },
        { label: 'Canada Way', value: '6049' },
        { label: 'Claude Avenue', value: '6050' },
        { label: 'Community Eight', value: '6048' },
        { label: 'Land Use', value: '20515' },
        { label: 'Single and Two Family Residential', value: '20516' },
        { label: 'Medium Density Multiple Family Residential', value: '20517' },
        { label: 'Low or Medium Multiple Family Residential', value: '20518' },
        { label: 'High Density Multiple Family Residential', value: '20519' },
        { label: 'Medium Density Mixed Use', value: '20520' },
        { label: 'High Density Mixed Use', value: '20521' },
        { label: 'Commercial', value: '20522' },
        { label: 'Industrial', value: '20523' },
        { label: 'Institutional', value: '20524' },
        { label: 'Park and Public Use/Public School', value: '20525' },
        { label: 'Recreational Vehicle Park', value: '20526' },
        { label: 'Township of Langley', value: '3998' },
        { label: 'Official Community Plan', value: '4008' },
        { label: 'Aldergrove Community Plan', value: '3999' },
        { label: 'Brookswood-Fernridge Community Plan', value: '4000' },
        { label: 'Fort Langley Community Plan', value: '4003' },
        { label: 'Gloucester Community Plan', value: '4004' },
        { label: 'Langley Town Centre Community Plan', value: '4005' },
        { label: 'Murrayville Community Plan', value: '4006' },
        { label: 'Northwest Langley Community Plan', value: '4007' },
        { label: 'Rural Community Plan', value: '4009' },
        { label: 'Willowbrook Community Plan', value: '4012' },
        { label: 'Willoughby Community Plan', value: '4011' },
        { label: 'Neighbourhood Plan', value: '11709' },
        { label: 'Carvolth Neighbourhood Plan', value: '4001' },
        { label: 'Central Gordon Estate Neighbourhood Plan', value: '4002' },
        { label: 'Jericho Sub-Neighbourhood Plan', value: '4013' },
        { label: 'Latimer Neighbourhood Plan', value: '4014' },
        { label: 'North East Gordon Neighbourhood Plan', value: '4015' },
        { label: 'Routley Neighbourhood Plan', value: '4016' },
        { label: 'Smith Neighbourhood Plan', value: '4017' },
        { label: 'South West Gordon Neighbourhood Plan', value: '4018' },
        { label: 'Walnut Grove Community Plan', value: '4010' },
        { label: 'Walnut Grove Neighbourhood Plan - Redwoods', value: '4019' },
        { label: 'Williams Neighbourhood Plan', value: '4020' },
        { label: 'Yorkson Neighbourhood Plan', value: '4021' },
        { label: 'Land Use', value: '20499' },
        { label: 'Gateway', value: '20500' },
        { label: 'Office/Mixed Use I', value: '20501' },
        { label: 'Office/Mixed Use II', value: '20502' },
        { label: 'Office/Mixed Use III', value: '20503' },
        { label: 'Flex Employment I', value: '20504' },
        { label: 'Flex Employment II', value: '20505' },
        { label: 'Service Commercial', value: '20506' },
        { label: 'High Street Mixed Use', value: '20507' },
        { label: 'Work/Live Flex Use', value: '20508' },
        { label: 'Transit Exchange', value: '20509' },
        { label: 'High Density Residential', value: '20510' },
        { label: 'Medium Density Residential', value: '20511' },
        { label: 'Townhouse Residential', value: '20512' },
        { label: 'Conservation Area', value: '20513' },
        { label: 'Integrated Open Space', value: '20514' },
        { label: 'Coquitlam', value: '6051' },
        { label: 'Area Plan', value: '20446' },
        { label: 'City Centre Area Plan', value: '6055' },
        { label: 'Northeast Coquitlam Area Plan', value: '6054' },
        { label: 'Hazel-Coy', value: '20445' },
        { label: 'Lower Hyde Creek', value: '6060' },
        { label: 'Partington Creek', value: '6063' },
        { label: 'Smiling Creek', value: '6062' },
        { label: 'Upper Hyde Creek', value: '6061' },
        { label: 'Northwest Coquitlam Area Plan', value: '6053' },
        { label: 'Southwest Coquitlam Area Plan', value: '6052' },
        { label: 'Austin Heights', value: '6056' },
        { label: 'Burquitlam-Lougheed', value: '6057' },
        { label: 'Maillardville', value: '6058' },
        { label: 'Waterfront Village Centre', value: '6059' },
        { label: 'Land Use', value: '20447' },
        { label: 'Area Under Study', value: '20448' },
        { label: 'Estate Single Family', value: '20449' },
        { label: 'Baycrest Low Density Residential', value: '20450' },
        { label: 'Large Single Family', value: '20451' },
        { label: 'One Family Residential', value: '20452' },
        { label: 'Compact Low Density Residential', value: '20453' },
        { label: 'Compact One Family', value: '20454' },
        { label: 'Compact One Family Residential', value: '20455' },
        { label: 'Executive Single Family', value: '20456' },
        { label: 'Suburban Residential', value: '20457' },
        { label: 'Small Village Single Family', value: '20458' },
        { label: 'Large Village Single Family', value: '20459' },
        { label: 'Village - Low Density Residential', value: '20460' },
        { label: 'Village - Medium Density Residential', value: '20461' },
        { label: 'Village - High Density Residential', value: '20462' },
        { label: 'Street Oriented Village Home', value: '20463' },
        { label: 'Neighbourhood Attached Residential', value: '20464' },
        { label: 'Cluster Housing', value: '20465' },
        { label: 'Mobile Home Park', value: '20466' },
        { label: 'Conventional Townhomes', value: '20467' },
        { label: 'Townhousing', value: '20468' },
        { label: 'Urban Townhousing', value: '20469' },
        { label: 'Neighbourhood Centre', value: '20470' },
        { label: 'Low Density Apartment Residential', value: '20471' },
        { label: 'Medium Density Apartment Residential', value: '20472' },
        { label: 'High Density Apartment Residential', value: '20473' },
        { label: 'Waterfront Village Centre', value: '20474' },
        { label: 'Transit Village Commercial', value: '20475' },
        { label: 'Local Commercial', value: '20476' },
        { label: 'General Commercial', value: '20477' },
        { label: 'Neighbourhood Commercial', value: '20478' },
        { label: 'City Centre Commercial', value: '20479' },
        { label: 'Service Commercial', value: '20480' },
        { label: 'Industrial', value: '20481' },
        { label: 'Business Enterprise', value: '20482' },
        { label: 'Agricultural', value: '20483' },
        { label: 'Civic and Major Institutional', value: '20484' },
        { label: 'School', value: '20485' },
        { label: 'School/Park', value: '20486' },
        { label: 'Environmentally Sensitive Area', value: '20487' },
        { label: 'Natural Areas', value: '20488' },
        { label: 'Open Space', value: '20489' },
        { label: 'Parks and Recreation', value: '20490' },
        { label: 'Neighbourhood Parks & Natural Open Spaces (NECAP)', value: '20491' },
        { label: 'Extensive Recreation', value: '20492' },
        { label: 'Linear Park', value: '20493' },
        { label: 'Rural Resource', value: '20494' },
        { label: 'Rural Resource and Residential (NECAP)', value: '20495' },
        { label: 'Development Reserve', value: '20496' },
        { label: 'Protected Resource - Watershed', value: '20497' },
        { label: 'Special Study Area', value: '20498' },
        { label: 'Richmond', value: '18844' },
        { label: 'Area Plan', value: '20428' },
        { label: 'Blundell Area', value: '18845' },
        { label: 'Laurelwood Sub-Area Plan', value: '18846' },
        { label: 'East Livingstone Sub-Area Plan', value: '18847' },
        { label: 'Bridgeport Area', value: '18848' },
        { label: 'Bridgeport Area Plan', value: '18849' },
        { label: 'Broadmoor Area', value: '18850' },
        { label: 'Ash Street Sub-Area Plan', value: '18851' },
        { label: 'Central West Sub-Area Plan', value: '18852' },
        { label: 'Sunnymede North Sub-Area Plan', value: '18853' },
        { label: 'City Centre Area', value: '18860' },
        { label: 'St. Albans Sub-Area Plan', value: '18854' },
        { label: 'Acheson-Bennett Sub-Area Plan', value: '18855' },
        { label: 'McLennan North Sub-Area Plan', value: '18856' },
        { label: 'McLennan South Sub-Area Plan', value: '18857' },
        { label: 'East Cambie Area', value: '18858' },
        { label: 'East Richmond Area Plan', value: '18859' },
        { label: 'East Richmond Area', value: '18861' },
        { label: 'McLennan Sub-Area Plan', value: '18862' },
        { label: 'Hamilton Area', value: '18863' },
        { label: 'Hamilton Area Plan', value: '18864' },
        { label: 'Shellmont Area', value: '18865' },
        { label: 'Ironwood Sub-Area Plan', value: '18866' },
        { label: 'Steveston Area', value: '18867' },
        { label: 'Steveston Area Plan', value: '18868' },
        { label: 'Thompson Area', value: '18869' },
        { label: 'Dover Crossing Sub-Area Plan', value: '18870' },
        { label: 'Terra Nova Sub-Area Plan', value: '18871' },
        { label: 'West Cambie Area', value: '18872' },
        { label: 'West Cambie Area Plan', value: '18873' },
        { label: 'Land Use', value: '20429' },
        { label: 'Agriculture', value: '20430' },
        { label: 'Airport', value: '20431' },
        { label: 'Apartment Residential', value: '20432' },
        { label: 'Commercial', value: '20433' },
        { label: 'Community Institutional', value: '20434' },
        { label: 'Conservation Area', value: '20435' },
        { label: 'Downtown Mixed use', value: '20436' },
        { label: 'Industrial', value: '20437' },
        { label: 'Limited Mixed Use', value: '20438' },
        { label: 'Mixed Employment', value: '20439' },
        { label: 'Mixed Use', value: '20440' },
        { label: 'Neighbourhood Residential', value: '20441' },
        { label: 'Neighbourhood Service Centre', value: '20442' },
        { label: 'Park', value: '20443' },
        { label: 'School', value: '20444' },
        { label: 'District of North Vancouver', value: '9485' },
        { label: 'Local Area Plans', value: '10652' },
        { label: 'Lower Capilano Local Plan', value: '10653' },
        { label: 'Alpine Area Plan', value: '20427' },
        { label: 'Lower Lynn Local Plan', value: '10654' },
        { label: 'Lynn Valley Local Plan', value: '10655' },
        { label: 'Lynnmour/Inter-River Area One Design Guidelines', value: '10662' },
        { label: 'Lynnmour/Inter-River Local Plan', value: '10656' },
        { label: 'Maplewood Local Plan', value: '10657' },
        { label: 'North Lonsdale-Delbrook Official Community Plan', value: '10658' },
        { label: 'Seymour Local Plan', value: '10659' },
        { label: 'Upper Capilano Local Plan Part 1', value: '10660' },
        { label: 'Upper Capilano Local Plan Part 2', value: '10661' },
        { label: 'Land Use', value: '9495' },
        { label: 'Residential Level 1: Rural Residential', value: '9496' },
        { label: 'Residential Level 2: Detached Residential', value: '9497' },
        { label: 'Residential Level 3: Attached Residential', value: '9498' },
        { label: 'Residential Level 4: Transition Multifamily', value: '9499' },
        { label: 'Residential Level 5: Low Density Apartment', value: '9500' },
        { label: 'Residential Level 6: Medium Density Apartment', value: '9501' },
        { label: 'Commercial', value: '9502' },
        { label: 'Commercial Residential Mixed Use Level 1', value: '9503' },
        { label: 'Commercial Residential Mixed Use Level 2', value: '9504' },
        { label: 'Commercial Residential Mixed Use Level 3', value: '9505' },
        { label: 'Institutional', value: '9506' },
        { label: 'Industrial', value: '9507' },
        { label: 'Light Industrial Commercial', value: '9508' },
        { label: 'Light Industrial Artisan', value: '9509' },
        { label: 'Light Industrial Commercial Mixed Use - Innovation District', value: '9510' },
        { label: 'Light Industrial Residential Mixed Use – Innovation District', value: '9511' },
        { label: 'Parks, Open Space, and Natural Areas', value: '9512' },
        { label: 'Victoria', value: '11264' },
        { label: 'Official Community Plan', value: '11341' },
        { label: 'Core Business', value: '11480' },
        { label: 'Urban Residential', value: '11481' },
        { label: 'Downtown Core Area Plan', value: '11360' },
        { label: 'Local Area and Neighbourhood Plans', value: '11340' },
        { label: 'Burnside', value: '11328' },
        { label: 'Downtown / Harris Green', value: '11291' },
        { label: 'Fairfield', value: '11329' },
        { label: 'Fernwood', value: '11330' },
        { label: 'Gonzales', value: '11331' },
        { label: 'Hillside Quadra', value: '11332' },
        { label: 'James Bay', value: '11333' },
        { label: 'Jubilee', value: '11334' },
        { label: 'North Park', value: '11335' },
        { label: 'Oaklands', value: '11336' },
        { label: 'Rockland', value: '11337' },
        { label: 'Victoria Harbour', value: '11338' },
        { label: 'Victoria West', value: '11339' },
        { label: 'Abbotsford', value: '6428' },
        { label: 'OCP/NCP', value: '7514' },
        { label: 'City Centre', value: '6429' },
        { label: 'Historic Downtown', value: '6430' },
        { label: 'UDistrict', value: '6431' },
        { label: 'Land Use', value: '7515' },
        { label: 'Mixed-Use Centres', value: '7516' },
        { label: 'City Centre', value: '7517' },
        { label: 'Urban Centre', value: '6433' },
        { label: 'Neighbourhood Centre', value: '6434' },
        { label: 'Residential Neighbourhoods', value: '7518' },
        { label: 'Urban 1 - Midrise', value: '6435' },
        { label: 'Urban 2 - Ground Oriented', value: '6436' },
        { label: 'Urban 3 - Infill', value: '6437' },
        { label: 'Urban 4 - Detached', value: '6438' },
        { label: 'Suburban', value: '6427' },
        { label: 'Employment Lands', value: '7519' },
        { label: 'Secondary Commercial', value: '6439' },
        { label: 'Regional Commercial', value: '6440' },
        { label: 'General Industrial', value: '6441' },
        { label: 'High Impact Industrial', value: '6442' },
        { label: 'Health Campus', value: '6443' },
        { label: 'Supporting Lands', value: '7520' },
        { label: 'Institutional Complex', value: '6444' },
        { label: 'Institutional', value: '6445' },
        { label: 'City of North Vancouver', value: '10303' },
        { label: 'Land Use', value: '10304' },
        { label: 'Residential Level 1 (Low Density)', value: '10305' },
        { label: 'Residential Level 2 (Low Density)', value: '10306' },
        { label: 'Residential Level 3 (Low Density)', value: '10307' },
        { label: 'Residential Level 4A (Medium Density)', value: '10308' },
        { label: 'Residential Level 4B (Medium Density)', value: '10309' },
        { label: 'Residential Level 5 (Medium Density)', value: '10310' },
        { label: 'Residential Level 6 (High Density)', value: '10311' },
        { label: 'Mixed Use Level 1 (Low Density)', value: '10312' },
        { label: 'Mixed Use Level 2 (Medium Density)', value: '10313' },
        { label: 'Mixed Use Level 3 (Medium Density)', value: '10314' },
        { label: 'Mixed Use Level 4A (High Density)', value: '10315' },
        { label: 'Mixed Use Level 4B (High Density)', value: '10316' },
        { label: 'Harbourside Waterfront (Mixed Use)', value: '10317' },
        { label: 'Parks, Recreation & Open Space', value: '10318' },
        { label: 'School & Institutional', value: '10319' },
        { label: 'Commercial', value: '10320' },
        { label: 'Mixed Employment', value: '10321' },
        { label: 'Industrial', value: '10322' },
        { label: 'Port Coquitlam', value: '8122' },
        { label: 'Land Use', value: '8123' },
        { label: 'SC - School', value: '8144' },
        { label: 'AH - High Density Apartment Residential', value: '8140' },
        { label: 'MT - Marine Traditional Commercial', value: '8135' },
        { label: 'MC - Marine Commercial', value: '8129' },
        { label: 'L - Local Commercial', value: '8128' },
        { label: 'IL - Light Industrial', value: '8134' },
        { label: 'IH - Heavy Industrial', value: '8133' },
        { label: 'IG - General Industrial', value: '8132' },
        { label: 'A - Apartment Residential', value: '8139' },
        { label: 'I - Institutional', value: '8131' },
        { label: 'FTD - Frequent Transit Development', value: '8127' },
        { label: 'AGR - Agricultural', value: '8124' },
        { label: 'D - Downtown Commercial', value: '8125' },
        { label: 'N - Neighbourhood Commercial', value: '8130' },
        { label: 'CH - Highway Commercial', value: '8126' },
        { label: 'RT - Townhouse Residential', value: '8142' },
        { label: 'P - Park and Recreation', value: '8136' },
        { label: 'R - Residential', value: '8138' },
        { label: 'RSL - Small Lot Residential', value: '8141' },
        { label: 'RC - Comprehensive Residential', value: '8143' },
        { label: 'PR - Park Reserve', value: '8137' },
        { label: 'City of Langley', value: '4030' },
        { label: 'Land Use', value: '20617' },
        { label: 'Agricultural', value: '4038' },
        { label: 'Ground Oriented', value: '13024' },
        { label: 'Sub-Urban', value: '13021' },
        { label: 'Transit-Oriented Core', value: '13022' },
        { label: 'Transit-Oriented Residential', value: '13023' },
        { label: 'Downtown Commercial', value: '4034' },
        { label: 'Estate Residential', value: '4032' },
        { label: 'High Density Residential', value: '4042' },
        { label: 'Industrial', value: '4037' },
        { label: 'Institutional', value: '4039' },
        { label: 'Low Density Residential', value: '4040' },
        { label: 'Medium Density Residential', value: '4041' },
        { label: 'Mixed Employment', value: '4036' },
        { label: 'Multiple Family Residential', value: '4033' },
        { label: 'Service Commercial', value: '4035' },
        { label: 'Urban Residential', value: '4031' }
      ],
    };
  },
  watch: {
    filterText(v) {
      this.applyTabulatorFilter(v);
    },
  },
  mounted() {
    this.fetchLandListings();
    this.loadFilters();
  },
  methods: {
    isEditableField(key) {
      return EDITABLE_FIELDS.includes(key);
    },

    setListValues(key) {
      if (key === 'field_land_type') return this.landTypeOptions;
      if (key === 'field_bc_assessment_sale_type') return this.saleTypeOptions;
      if (key === 'field_existing_use_ref') return this.existingUseOptions;
      if (key === 'field_city_based_planning_design') return this.cityBasedPlanningDesignationOptions;
      return [];
    },

    // ── Columns ───────────────────────────────────────────────────────────
    // Built from `fields`, so the Fields panel's order is the table's order.
    buildColumns() {
      const dataCols = this.fields.map((f) => this.columnDef(f));
      // The Save column only exists where there is something to save: no other
      // role is given a cell editor.
      return wrapColumns(this.isAdmin ? [this.saveColumnDef(), ...dataCols] : dataCols);
    },

    columnDef(f) {
      const isNumeric = NUMERIC_FIELDS.includes(f.key);
      const isDate = DATE_FIELDS.includes(f.key);
      const isList = LIST_FIELDS.includes(f.key);
      const right = f.align === 'right';

      const def = {
        title: f.label,
        field: f.key,
        minWidth: f.width || 130,
        hozAlign: right ? 'right' : 'left',
        headerHozAlign: right ? 'right' : 'left',
        sorter: isNumeric ? 'number' : isDate ? 'date' : 'string',
        formatter: this.cellFormatter(f.key),
      };

      if (f.key === 'field_legal_description') {
        def.cellClick = (e, cell) => {
          if (!e.target.classList.contains('ms-readmore')) return;
          e.preventDefault();
          e.stopPropagation();
          this.showLegalModal(cell.getValue());
        };
      }

      if (!this.isAdmin || !EDITABLE_FIELDS.includes(f.key)) return def;

      // Tinted header (ms-col-editable) so an admin can see what they can
      // change before double-clicking into it.
      return {
        ...def,
        cssClass: 'ms-col-editable',
        editor: isList ? 'list' : isDate ? 'date' : 'input',
        editorParams: isList
          ? {
            values: this.setListValues(f.key),
            // Planning designation runs to several hundred entries, so the
            // list is typed into rather than scrolled.
            autocomplete: true,
            allowEmpty: true,
            listOnEmpty: true,
            verticalNavigation: 'editor',
          }
          : {},
      };
    },

    // Stays empty until a row has unsaved inline edits, then offers the button
    // that posts them (see handleRowSave).
    saveColumnDef() {
      return {
        title: "",
        field: "isEdited",
        width: 62,
        minWidth: 62,
        resizable: false,
        headerSort: false,
        frozen: true,
        cssClass: "ms-col-save",
        formatter: (cell) =>
          cell.getRow().getData().isEdited
            ? '<button class="ms-save-btn" title="Save changes">Save</button>'
            : "",
        cellClick: (e, cell) => {
          if (!cell.getRow().getData().isEdited) return;
          e.stopPropagation();
          this.handleRowSave(cell.getRow());
        },
      };
    },

    cellFormatter(key) {
      return (cell) => {
        const value = cell.getValue();
        if (value === null || value === undefined || value === '') return dash();

        if (key === 'field_lot_size_sqft_') {
          const n = Number(value);
          return isFinite(n) && n !== 0 ? this.formatNumber(n) : dash();
        }
        if (CURRENCY_FIELDS.includes(key)) {
          const n = Number(value);
          return isFinite(n) && n !== 0 ? this.formatCurrency(n) : dash();
        }
        if (key === 'field_site_address') {
          const { project } = cell.getRow().getData();
          const label = escapeHtml(value);
          return project
            ? `<a class="ms-pname ms-plink" target="_blank" rel="noopener noreferrer" href="/project?project=${encodeURIComponent(project)}">${label}</a>`
            : `<span class="ms-pname">${label}</span>`;
        }
        if (key === 'field_legal_description' && String(value).length > 40) {
          return `<span>${escapeHtml(String(value).slice(0, 40))}… <a href="#" class="ms-readmore">Read more</a></span>`;
        }
        return escapeHtml(value);
      };
    },

    // ── Table ─────────────────────────────────────────────────────────────
    initTabulator() {
      if (!this.$refs.table) return;
      if (this.tabulator) this.tabulator.destroy();

      this.tabulator = markRaw(new Tabulator(this.$refs.table, {
        data: this.items,
        layout: "fitDataStretch",
        height: "100%",
        placeholder: "<div class='ms-placeholder'>No land listings found</div>",
        reactiveData: false,
        // Admin inline editing only — no column carries an editor for other
        // roles, so this is inert for them.
        editTriggerEvent: "dblclick",
        rowHeight: 42,
        pagination: "local",
        paginationSize: 25,
        paginationSizeSelector: [15, 25, 50, 100],
        columns: this.buildColumns(),
        dataFiltered: (filters, rows) => {
          this.rowCount = rows.length;
        },
      }));

      this.tabulator.on("cellEdited", (cell) => {
        const row = cell.getRow();
        const data = row.getData();
        // Only the fields actually touched are posted, so an untouched column
        // can never be overwritten by a save.
        const dirtyFields = { ...(data.dirtyFields || {}), [cell.getField()]: true };
        row.update({ isEdited: true, dirtyFields });
        row.reformat();
      });

      if (this.filterText) this.applyTabulatorFilter(this.filterText);
    },

    // A data point moved between the two lists, or was reordered inside one.
    // setColumns rather than a rebuild: the table keeps its page and its sort.
    fieldsOn() {
      if (this.tabulator) this.tabulator.setColumns(this.buildColumns());
    },

    resetFields() {
      this.fields = DEFAULT_FIELDS.map((f) => ({ ...f }));
      this.rpdTableList2 = [];
      this.fieldsOn();
    },

    applyTabulatorFilter(v) {
      if (!this.tabulator) return;
      if (!v) return this.tabulator.clearFilter();
      // One OR group: a row matches when any searchable column contains the term.
      this.tabulator.setFilter([
        SEARCH_FIELDS.map((field) => ({ field, type: "like", value: v })),
      ]);
    },

    exportCsv() {
      if (this.tabulator) this.tabulator.download("csv", "land-listings.csv");
    },

    showLegalModal(text) {
      this.selectedLegalDescription = text;
      this.$bvModal.show('modal-legal');
    },

    // ── Saving inline edits ───────────────────────────────────────────────
    async handleRowSave(row) {
      const rowData = row.getData();
      const dirtyFields = rowData.dirtyFields || {};
      const touched = EDITABLE_FIELDS.filter((field) => dirtyFields[field]);

      if (!touched.length) {
        this.makeToast('Info', 'No changes detected.', 'info');
        return;
      }

      // Both endpoints rewrite their own whole record, so each carries the
      // fields that identify it whether or not those were the ones edited.
      const saleForm = new FormData();
      saleForm.append('land_id', rowData.land_id);
      saleForm.append('field_site_address', rowData.field_site_address);
      saleForm.append('field_ltsa_pid', rowData.field_ltsa_pid);
      saleForm.append('field_lot_size_sqft_', rowData.field_lot_size_sqft_);
      saleForm.append('field_legal_description', rowData.field_legal_description);

      const registrationForm = new FormData();
      registrationForm.append('title_registration', rowData.title_registration);

      const registrationFields = touched.filter((f) => !LAND_SALE_FIELDS.includes(f));
      registrationFields.forEach((field) => registrationForm.append(field, rowData[field]));

      const hasSaleEdit = touched.some((f) => LAND_SALE_FIELDS.includes(f));

      try {
        const requests = [];
        if (registrationFields.length) {
          requests.push(axios.post(`${BACKEND_URL}api/land_title_registration/edit`, registrationForm));
        }
        if (hasSaleEdit) {
          requests.push(axios.post(`${BACKEND_URL}api/land_sale/edit`, saleForm));
        }
        await Promise.all(requests);

        this.makeToast('Success', 'Changes saved successfully.', 'success');
        row.update({ isEdited: false, dirtyFields: {} });
        row.reformat();
        this.syncDataChange(row.getData());
      } catch (error) {
        console.error("Save Error:", error);
        this.makeToast('Error', 'Failed to save changes.', 'danger');
      }
    },

    // Keeps `items` in step with the row that was just saved, so rebuilding the
    // table does not resurrect the pre-edit values.
    syncDataChange(updatedRow) {
      const index = this.items.findIndex(
        (item) => item.project === updatedRow.project
          && item.field_site_address === updatedRow.field_site_address
      );
      if (index !== -1) this.$set(this.items, index, { ...updatedRow, isEdited: false });
    },

    makeToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, { title, variant, solid: true, autoHideDelay: 3000 });
    },

    // ── Saved views ───────────────────────────────────────────────────────
    async saveFilters() {
      try {
        await saveView(this.getViewCategory, "field_selected", { field_selected: this.fields });
        await saveView(this.getViewCategory, "custom_headers", { custom_headers: this.rpdTableList2 });
        this.makeToast('Great job!', 'View saved successfully.', 'success');
      } catch (error) {
        console.error("Save view error:", error);
        this.makeToast('Ooops!', 'Saving the view failed. Please contact the administrator.', 'warning');
      }
    },

    async loadFilters() {
      try {
        const [shown, hidden] = await Promise.all([
          loadView(this.getViewCategory, "field_selected"),
          loadView(this.getViewCategory, "custom_headers"),
        ]);
        if (!Array.isArray(shown) || !Array.isArray(hidden)) return;

        // Only restore a view that still describes this table. A data point
        // added since the save would otherwise never appear, and one removed
        // would come back as an empty column.
        const savedKeys = [...shown, ...hidden].map((f) => f && f.key).filter(Boolean);
        const knownKeys = DEFAULT_FIELDS.map((f) => f.key);
        const describesTable = savedKeys.length === knownKeys.length
          && knownKeys.every((key) => savedKeys.includes(key));
        if (!describesTable) return;

        // A saved view records the order the reader chose; the label and width
        // come from the current definition, which may have moved on since.
        const byKey = new Map(DEFAULT_FIELDS.map((f) => [f.key, f]));
        this.fields = shown.map((f) => ({ ...byKey.get(f.key) }));
        this.rpdTableList2 = hidden.map((f) => ({ ...byKey.get(f.key) }));
        this.fieldsOn();
      } catch (error) {
        console.error("Load view error:", error);
      }
    },

    // ── Data ──────────────────────────────────────────────────────────────
    async fetchLandListings() {
      this.loading = true;
      this.error = "";

      const cityQuery = this.$route.query.city;
      const cityString = Array.isArray(cityQuery)
        ? cityQuery.reduce((acc, curr) => acc + `city%5B%5D=${curr}&`, '')
        : (cityQuery ? `city%5B%5D=${cityQuery}` : "");

      try {
        const res = await fetch(`${BACKEND_URL}api/get_land_list?${cityString}`);
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
        const data = await res.json();

        const rows = (data || []).flatMap((item) => this.flattenLandItem(item));

        this.items = this.isAdmin
          ? rows
          : rows.filter((r) => r.field_land_list_price != null && r.field_land_sale_price != null);
        this.rowCount = this.items.length;
      } catch (error) {
        console.error("Land list fetch error:", error);
        this.error = "Could not load land listings. Please try again.";
        this.items = [];
        this.rowCount = 0;
      } finally {
        this.loading = false;
        // After the state flips, so the wrap is on screen and Tabulator can
        // measure the height it is sized against.
        this.$nextTick(() => this.initTabulator());
      }
    },

    // One row per title registration: PIDs and legal descriptions belong to the
    // parcel, so every registration on it repeats them.
    flattenLandItem(item) {
      const registrations = item.field_title_registration || [];
      const pids = (item.field_ltsa_pid || []).map((p) => p.value).filter(Boolean);
      const legals = (item.field_legal_description || []).map((l) => l.value).filter(Boolean);

      const toNum = (v) => {
        if (v === null || v === undefined || v === "") return null;
        const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
        return isNaN(n) ? null : n;
      };
      const toDate = (v) => (v ? String(v).split(' ')[0] : "");
      const toList = (v) => (Array.isArray(v) ? v.join(', ') : "");

      return registrations.map((reg) => ({
        land_id: item.land_id,
        project: item.project,
        field_site_address: item.field_site_address,
        field_lot_size_sqft_: toNum(item.field_lot_size_sqft_),
        field_ltsa_pid: pids.join(', '),
        field_legal_description: legals.join(', '),

        title_registration: reg.title_registration,
        field_bc_assessment_sale_type_tid: reg.field_bc_assessment_sale_type_tid,
        field_land_type_tid: reg.field_land_type_tid,
        field_existing_zoning_d_tid: reg.field_existing_zoning_d_tid,
        field_existing_use_ref_tid: reg.field_existing_use_ref_tid,
        field_city_based_planning_design_tid: reg.field_city_based_planning_design_tid,

        field_bc_assessment_sale_type: reg.field_bc_assessment_sale_type || "",
        field_land_type: reg.field_land_type || "",
        field_ltsa_registered_owner: reg.field_ltsa_registered_owner || "",
        field_existing_use_ref: reg.field_existing_use_ref || "",
        field_existing_zoning_d: reg.field_existing_zoning_d || "",
        field_city_based_planning_design: reg.field_city_based_planning_design || "",

        field_land_list_date: toDate(reg.field_land_list_date),
        field_sold_firm_date: toDate(reg.field_sold_firm_date),

        field_land_list_price: toNum(reg.field_land_list_price),
        field_land_sale_price: toNum(reg.field_land_sale_price),

        field_first_broker_agent: toList(reg.field_first_broker_agent),
        field_sold_broker_agent: toList(reg.field_sold_broker_agent),

        isEdited: false,
      }));
    },

    formatNumber(v) {
      return new Intl.NumberFormat('en-CA').format(v);
    },
    formatCurrency(v) {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 0,
      }).format(v);
    },
  },
  beforeDestroy() {
    if (this.tabulator) this.tabulator.destroy();
  },
  computed: {
    ...mapState(useUserStore, ['current_user']),
    isAdmin() {
      return this.current_user?.role === 'admin';
    },
    getViewCategory() {
      return 44;
    },
  },
}
</script>

<style scoped>
/* The page furniture below mirrors MarketOverview.vue's `ms-` shell so the two
   report pages read as one product: same header block, same filter bar, same
   fields panel, same table frame. Keep them in step when either moves. */
.ms-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f4f5f7;
}

.ms-hidden {
  display: none;
}

/* ── Header ── */
.ms-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 28px 16px;
  background: #ffffff;
  border-bottom: 1px solid #e9e8e9;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 10px;
}

.ms-header-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ms-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.ms-breadcrumb {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #8a8a8a;
}

.ms-breadcrumb-sep {
  color: #c9ccd2;
  font-size: 12px;
}

.ms-breadcrumb-cur {
  color: #12283f;
}

.ms-title-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  row-gap: 8px;
  gap: 12px;
}

.ms-title {
  font-family: "Poppins", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #12283f;
  letter-spacing: -0.02em;
  margin: 0;
  line-height: 1.1;
}

.ms-count {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
  white-space: nowrap;
}

/* ── Header buttons ── */
.ms-export-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  background: transparent;
  cursor: pointer;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1);
  font-family: inherit;
  white-space: nowrap;
}

.ms-export-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.ms-export-btn--csv {
  border: 1px solid #e2e4ea;
  color: #1f9d55;
}

.ms-export-btn--csv:hover:not(:disabled) {
  background: rgba(31, 157, 85, 0.06);
}

.ms-save-view-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #16a34a;
  border-radius: 6px;
  background: transparent;
  color: #16a34a;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.ms-save-view-btn:hover {
  background: #16a34a;
  color: #ffffff;
}

.ms-fields-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 6px;
  color: #545454;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);
  font-family: inherit;
  white-space: nowrap;
}

.ms-fields-btn:hover {
  background: #f7f8fb;
  border-color: #c9ccd2;
  color: #12283f;
}

.ms-fields-btn--active {
  background: #12283f;
  border-color: #12283f;
  color: #fff;
}

.ms-fields-btn--active:hover {
  background: #12283f;
  border-color: #12283f;
  color: #fff;
}

/* ── Body wrapper ── */
.ms-body {
  flex: 1;
  min-height: 0;
  padding: 20px 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-x: hidden;
  overflow-y: auto;
}

/* ── Filter bar ── */
.ms-filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e9e8e9;
  border-radius: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.ms-filter-icon {
  color: #9ca3af;
  font-size: 13px;
  flex-shrink: 0;
}

.ms-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 300px;
  padding: 6px 10px;
  border: 1px solid #e2e4ea;
  border-radius: 6px;
  background: #fafbfd;
  gap: 8px;
  flex-shrink: 0;
}

.ms-search-icon {
  color: #8a8a8a;
  font-size: 12px;
  flex-shrink: 0;
}

.ms-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: #12283f;
  font-family: inherit;
  min-width: 0;
}

.ms-filter-clear {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #f87171;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #dc2626;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: background 0.15s;
  flex-shrink: 0;
}

.ms-filter-clear:hover {
  background: #fee2e2;
}

/* ── Fields panel ── */
.ms-fields-panel {
  display: flex;
  background: #ffffff;
  border: 1px solid #e9e8e9;
  border-radius: 10px;
  flex-shrink: 0;
  max-height: 320px;
}

.ms-fields-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px 16px;
}

.ms-fields-col-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.ms-fields-col-title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  flex: 1;
}

.ms-fields-reset,
.ms-fields-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  color: #6b7280;
  font-size: 13px;
}

.ms-fields-reset:hover {
  color: #0e1b2c;
}

.ms-fields-divider {
  width: 1px;
  background: #e8eaed;
  flex-shrink: 0;
  margin: 12px 0;
}

.ms-fields-hint {
  font-size: 11px;
  color: #9ca3af;
  padding-top: 6px;
}

/* ── Draggable lists ── */
.ms-drag-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-content: flex-start;
  min-height: 48px;
}

.ms-drag-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #f9fafb;
  border: 1px solid #e8eaed;
  border-radius: 6px;
  font-size: 12px;
  color: #374151;
  cursor: grab;
  user-select: none;
  white-space: nowrap;
  transition: background 0.1s, border-color 0.1s;
}

.ms-drag-item:active {
  cursor: grabbing;
}

.ms-drag-item:hover {
  background: #f0f1f3;
  border-color: #d1d5db;
}

.ms-drag-item--active {
  background: #0e1b2c;
  color: #ffffff;
  border-color: #0e1b2c;
}

.ms-drag-item--active:hover {
  background: #1a2e47;
  border-color: #1a2e47;
}

.ms-drag-handle {
  color: inherit;
  opacity: 0.4;
  font-size: 11px;
}

.ms-drag-index {
  font-size: 10px;
  opacity: 0.6;
}

/* Marks a data point that admins can edit inline in the table */
.ms-drag-editable {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  cursor: help;
}

.ms-drag-editable svg {
  font-size: 8px;
}

/* The shown list uses dark chips, so the badge inverts there for contrast */
.ms-drag-item--active .ms-drag-editable {
  background: rgba(147, 197, 253, 0.2);
  color: #93c5fd;
}

/* ── Slide transition ── */
.ms-slide-enter-active,
.ms-slide-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
  overflow: hidden;
}

.ms-slide-enter,
.ms-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.ms-slide-enter-to,
.ms-slide-leave {
  max-height: 320px;
  opacity: 1;
}

/* ── States ── */
.ms-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  min-height: 240px;
  color: #9ca3af;
  font-size: 13px;
}

.ms-state--error {
  color: #dc2626;
}

.ms-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top-color: #0e1b2c;
  border-radius: 50%;
  animation: ms-spin 0.7s linear infinite;
}

@keyframes ms-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Table wrap ── */
/* min-height rather than 0: Tabulator is sized at height 100% of this wrap, so
   the wrap must keep a definite, usable height. Anything past that scrolls the
   body. */
.ms-table-wrap {
  flex: 1;
  overflow: hidden;
  min-height: 400px;
  background: #fff;
  border: 1px solid #e9e8e9;
  border-radius: 10px;
}

.ms-legal-text {
  padding: 12px;
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #374151;
}

/* ── Responsive: tablet ── */
@media (max-width: 991.98px) {
  .ms-header {
    padding: 12px 16px 14px;
  }

  .ms-body {
    padding: 14px 16px 16px;
  }

  .ms-title {
    font-size: 22px;
  }

  .ms-search-wrap {
    width: 100%;
  }

  .ms-fields-panel {
    flex-direction: column;
    max-height: none;
  }

  .ms-fields-divider {
    width: auto;
    height: 1px;
    margin: 0 12px;
  }
}

/* ── Responsive: mobile ── */
@media (max-width: 575.98px) {
  .ms-header {
    padding: 10px 12px 12px;
    gap: 8px;
  }

  .ms-header-right {
    width: 100%;
    gap: 6px;
  }

  .ms-title {
    font-size: 20px;
  }

  .ms-body {
    padding: 12px 12px 14px;
    gap: 10px;
  }

  .ms-table-wrap {
    min-height: 320px;
  }
}

/* ── Dark mode ── */
html.dark .ms-page {
  background: #0b1625;
}

html.dark .ms-header {
  background: #0e1b2c;
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

html.dark .ms-breadcrumb {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .ms-breadcrumb-cur {
  color: rgba(255, 255, 255, 0.65);
}

html.dark .ms-title {
  color: #d6e4f0;
}

html.dark .ms-count {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .ms-export-btn--csv {
  color: #34d399;
  border-color: #34d399;
}

html.dark .ms-export-btn--csv:hover:not(:disabled) {
  background: rgba(52, 211, 153, 0.1);
}

html.dark .ms-save-view-btn {
  color: #4ade80;
  border-color: #16a34a;
}

html.dark .ms-save-view-btn:hover {
  background: #16a34a;
  color: #ffffff;
}

html.dark .ms-fields-btn {
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.2);
}

html.dark .ms-fields-btn:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.4);
  color: rgba(255, 255, 255, 0.85);
}

html.dark .ms-fields-btn--active,
html.dark .ms-fields-btn--active:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.4);
  color: #fff;
}

html.dark .ms-filter-bar {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.07);
}

html.dark .ms-search-wrap {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
}

html.dark .ms-search {
  color: #d6e4f0;
}

html.dark .ms-search-icon {
  color: rgba(255, 255, 255, 0.25);
}

html.dark .ms-fields-panel {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.07);
}

html.dark .ms-fields-col-title {
  color: rgba(255, 255, 255, 0.4);
}

html.dark .ms-fields-divider {
  background: rgba(255, 255, 255, 0.07);
}

html.dark .ms-fields-hint {
  color: rgba(255, 255, 255, 0.3);
}

html.dark .ms-fields-reset,
html.dark .ms-fields-close {
  color: rgba(255, 255, 255, 0.4);
}

html.dark .ms-drag-item {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}

html.dark .ms-drag-item:hover {
  background: #1a2a3f;
}

html.dark .ms-drag-item--active {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

html.dark .ms-drag-editable,
html.dark .ms-drag-item--active .ms-drag-editable {
  background: rgba(96, 165, 250, 0.18);
  color: #93c5fd;
}

html.dark .ms-state {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .ms-spinner {
  border-color: rgba(255, 255, 255, 0.08);
  border-top-color: #4db7fe;
}

html.dark .ms-table-wrap {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.07);
}

html.dark .ms-legal-text {
  color: rgba(255, 255, 255, 0.75);
}
</style>

<style>
/* Tabulator itself is not scoped-styleable — its DOM is built outside the
   component tree — so the table skin lives here, keyed off .ms-table-wrap.
   Same skin as MarketOverview.vue's table. */

/* ── Tabulator reset ── */
.ms-table-wrap .tabulator {
  border: none !important;
  background: #ffffff !important;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI",
    sans-serif !important;
  font-size: 11px !important;
}

.ms-table-wrap .tabulator-header {
  background: #f8f9fa !important;
  border-bottom: 1px solid #e8eaed !important;
  border-top: none !important;
}

.ms-table-wrap .tabulator-col {
  background: transparent !important;
  border-right: none !important;
  padding: 0 !important;
}

.ms-table-wrap .tabulator-col-content {
  padding: 10px 14px !important;
}

.ms-table-wrap .tabulator-col-title {
  font-size: 10px !important;
  font-weight: 600 !important;
  color: #6b7280 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.06em !important;
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: clip !important;
}

.ms-table-wrap .tabulator-col.tabulator-sortable:hover .tabulator-col-title {
  color: #0e1b2c !important;
}

.ms-table-wrap .tabulator-col.tabulator-col-sorter-element:hover {
  background: #f0f1f3 !important;
}

.ms-table-wrap .tabulator-col .tabulator-col-sorter {
  color: #d1d5db !important;
}

.ms-table-wrap .tabulator-col.tabulator-col-sorter-element .tabulator-col-sorter {
  color: #0e1b2c !important;
}

.ms-table-wrap .tabulator-tableHolder {
  overflow-x: auto !important;
  background: #ffffff !important;
}

.ms-table-wrap .tabulator-row {
  background: #ffffff !important;
  border-bottom: 1px solid #f3f4f6 !important;
  transition: background 0.1s !important;
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: stretch !important;
  overflow: visible !important;
}

.ms-table-wrap .tabulator-row:hover,
.ms-table-wrap .tabulator-row.tabulator-row-even:hover {
  background: #f9fafb !important;
}

.ms-table-wrap .tabulator-row.tabulator-row-even {
  background: #ffffff !important;
}

.ms-table-wrap .tabulator-cell {
  display: flex !important;
  align-items: center !important;
  flex-shrink: 0 !important;
  border-right: none !important;
  padding: 0 14px !important;
  color: #374151 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

.ms-table-wrap .tabulator-tableHolder::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.ms-table-wrap .tabulator-tableHolder::-webkit-scrollbar-track {
  background: transparent;
}

.ms-table-wrap .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 3px;
}

/* ── Footer / pagination ──
   Tabulator right-aligns the paginator by default, which parks it under the
   fixed ChatBotWidget (bottom-right, z-index 1080) and makes the last page
   buttons unclickable. Pull the controls to the left instead. */
.ms-table-wrap .tabulator-footer {
  background: #f8f9fa !important;
  border-top: 1px solid #e8eaed !important;
}

.ms-table-wrap .tabulator-footer .tabulator-footer-contents {
  justify-content: flex-start !important;
  /* Leave the widget its corner even when the paginator grows wide. */
  padding-right: 180px !important;
}

.ms-table-wrap .tabulator-footer .tabulator-paginator {
  flex: 0 1 auto !important;
  text-align: left !important;
  color: #6b7280 !important;
  font-size: 11px !important;
}

/* The launcher collapses to an icon on phones, so it needs far less room. */
@media (max-width: 575.98px) {
  .ms-table-wrap .tabulator-footer .tabulator-footer-contents {
    padding-right: 72px !important;
  }
}

/* ── Save column ── */
.ms-table-wrap .tabulator-cell.ms-col-save,
.ms-table-wrap .tabulator-col.ms-col-save {
  justify-content: center !important;
  padding: 0 !important;
}

.ms-save-btn {
  border: 1px solid #16a34a;
  background: #16a34a;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.ms-save-btn:hover {
  background: #15803d;
  border-color: #15803d;
}

/* Editable columns get a tinted header so admins can see what they can change */
.ms-table-wrap .tabulator-col.ms-col-editable,
.ms-table-wrap .tabulator-col.tabulator-frozen.ms-col-editable {
  background: #eff6ff !important;
  border-bottom: 2px solid #2563eb !important;
}

.ms-table-wrap .tabulator-col.ms-col-editable .tabulator-col-title {
  color: #1d4ed8 !important;
}

/* Cell editors (list, input, date) */
.ms-table-wrap .tabulator-cell.tabulator-editing input {
  width: 100%;
  height: 100%;
  border: 1px solid #2563eb;
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 12px;
  background: #ffffff;
  color: #0f172a;
  outline: none;
  box-sizing: border-box;
}

/* ── Frozen (sticky) columns ── */
.ms-table-wrap .tabulator-col.tabulator-frozen,
.ms-table-wrap .tabulator-cell.tabulator-frozen {
  z-index: 5;
  box-shadow: 2px 0 4px -2px rgba(15, 23, 42, 0.08);
}

.ms-table-wrap .tabulator-col.tabulator-frozen {
  background: #f8f9fa !important;
  z-index: 11;
}

.ms-table-wrap .tabulator-row .tabulator-cell.tabulator-frozen,
.ms-table-wrap .tabulator-row.tabulator-row-even .tabulator-cell.tabulator-frozen {
  position: sticky !important;
  z-index: 6 !important;
  background: #ffffff !important;
}

.ms-table-wrap .tabulator-row:hover .tabulator-cell.tabulator-frozen {
  background: #f9fafb !important;
}

/* ── Cell content ── */
.ms-table-wrap .tabulator-cell .ms-pname {
  font-size: 12px;
  font-weight: 500;
  color: #111827;
  line-height: 1.3;
}

.ms-table-wrap .tabulator-cell .ms-plink {
  color: #0e1b2c;
  text-decoration: none;
}

.ms-table-wrap .tabulator-cell .ms-plink:hover {
  text-decoration: underline;
  color: #1d4ed8;
}

.ms-table-wrap .tabulator-cell .ms-readmore {
  color: #1d4ed8;
  text-decoration: none;
  white-space: nowrap;
}

.ms-table-wrap .tabulator-cell .ms-readmore:hover {
  text-decoration: underline;
}

.ms-table-wrap .tabulator-cell .ms-empty {
  color: #d1d5db;
}

.ms-placeholder {
  padding: 48px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}

/* ── Dark mode ── */
html.dark .ms-table-wrap .tabulator,
html.dark .ms-table-wrap .tabulator-tableHolder {
  background: #0b1625 !important;
}

html.dark .ms-table-wrap .tabulator-header {
  background: #0e1b2c !important;
  border-bottom-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ms-table-wrap .tabulator-col-title {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ms-table-wrap .tabulator-col.tabulator-sortable:hover .tabulator-col-title {
  color: #d6e4f0 !important;
}

html.dark .ms-table-wrap .tabulator-col.tabulator-col-sorter-element:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

html.dark .ms-table-wrap .tabulator-row,
html.dark .ms-table-wrap .tabulator-row.tabulator-row-even {
  background: #0b1625 !important;
  border-bottom-color: rgba(255, 255, 255, 0.04) !important;
}

html.dark .ms-table-wrap .tabulator-row:hover,
html.dark .ms-table-wrap .tabulator-row.tabulator-row-even:hover {
  background: #16263d !important;
}

html.dark .ms-table-wrap .tabulator-row:hover .tabulator-cell {
  background-color: transparent !important;
}

html.dark .ms-table-wrap .tabulator-cell {
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ms-table-wrap .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}

html.dark .ms-table-wrap .tabulator-footer {
  background: #0e1b2c !important;
  border-top-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ms-table-wrap .tabulator-footer .tabulator-paginator,
html.dark .ms-table-wrap .tabulator-footer .tabulator-page,
html.dark .ms-table-wrap .tabulator-footer .tabulator-page-size {
  color: rgba(255, 255, 255, 0.6) !important;
  background: transparent !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

html.dark .ms-table-wrap .tabulator-footer .tabulator-page.active {
  color: #d6e4f0 !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

/* ── Frozen (sticky) columns — dark mode ── */
html.dark .ms-table-wrap .tabulator-col.tabulator-frozen,
html.dark .ms-table-wrap .tabulator-cell.tabulator-frozen {
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.5);
}

html.dark .ms-table-wrap .tabulator-col.tabulator-frozen {
  background: #0b1625 !important;
}

html.dark .ms-table-wrap .tabulator-row .tabulator-cell.tabulator-frozen,
html.dark .ms-table-wrap .tabulator-row.tabulator-row-even .tabulator-cell.tabulator-frozen {
  background: #0b1625 !important;
}

html.dark .ms-table-wrap .tabulator-row:hover .tabulator-cell.tabulator-frozen {
  background: #16263d !important;
}

/* ── Admin inline editing (dark) ── */
html.dark .ms-table-wrap .tabulator-col.ms-col-editable,
html.dark .ms-table-wrap .tabulator-col.tabulator-frozen.ms-col-editable {
  background: #16263d !important;
  border-bottom: 2px solid #60a5fa !important;
}

html.dark .ms-table-wrap .tabulator-col.ms-col-editable .tabulator-col-title {
  color: #93c5fd !important;
}

html.dark .ms-table-wrap .tabulator-cell.tabulator-editing input {
  background: #0b1625;
  border-color: #60a5fa;
  color: #d6e4f0;
}

html.dark .ms-table-wrap .tabulator-popup-container {
  background: #0f1e33 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #d6e4f0 !important;
}

html.dark .ms-table-wrap .tabulator-edit-list-item {
  color: #d6e4f0 !important;
}

html.dark .ms-table-wrap .tabulator-edit-list-item.active,
html.dark .ms-table-wrap .tabulator-edit-list-item:hover {
  background: #1e3a5f !important;
  color: #ffffff !important;
}

html.dark .ms-table-wrap .tabulator-cell .ms-pname {
  color: #d6e4f0;
}

html.dark .ms-table-wrap .tabulator-cell .ms-plink {
  color: #93c5fd;
}

html.dark .ms-table-wrap .tabulator-cell .ms-empty {
  color: rgba(255, 255, 255, 0.15);
}
</style>
